/* eslint-disable no-console */
import { CUSTOM_ENDPOINTS } from './config/endpoints';
import {
  MINDTICKLE_DOMAIN,
  EXCEPTION_URLS,
  EPOCH_TIME_PARAM,
  RE_LOGIN_REDIRECT_URL,
} from '~/components/Tools/config/constants';
const remotes = require('../../../remotes');

type FetchArgs = [RequestInfo, RequestInit?];

// Check if the host includes Mindtickle domain or current window host
const isMindtickleDomain = (host = ''): boolean =>
  host.includes(MINDTICKLE_DOMAIN) || host.includes(window.location.host);

// Check if the response URL matches any exception URLs
const isExceptionURL = (response: any): boolean =>
  EXCEPTION_URLS.some(exceptionUrl => response?.[exceptionUrl.type]?.includes(exceptionUrl.value));

// Get all GraphQL endpoints from remotes and combine with custom endpoints
const getEndpoints = () => {
  const endpoints = remotes.reduce((acc: string[], remote: any) => {
    const gqlEndpoint = remote.graphqlEndpoint;
    if (gqlEndpoint) {
      acc.push(gqlEndpoint);
    }
    return acc;
  }, []);
  return [...endpoints, ...CUSTOM_ENDPOINTS];
};

// Check if a URL is included in any of the endpoints
const isUrlInEndpoints = (url: string) => {
  const endpoints = getEndpoints();
  return endpoints.some(endpoint => url.includes(endpoint));
};

export const inFrame = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

// Handle 401 Unauthorized response
// eslint-disable-next-line complexity
const handleUnauthorizedError = (response: Response) => {
  if (response.status === 401) {
    const responseURL = new URL(response.url);
    const responseHost = responseURL.host;
    const urlWithoutQueryParams = `${responseURL.origin}${responseURL.pathname}`;

    if (
      (isUrlInEndpoints(urlWithoutQueryParams) || isMindtickleDomain(responseHost)) &&
      !isExceptionURL(responseURL) &&
      !inFrame()
    ) {
      // Redirect to login or handle the unauthorized error as needed
      const path = window.location.pathname;
      // eslint-disable-next-line max-depth
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(RE_LOGIN_REDIRECT_URL, path);
      }
      window.location.href = `/login?loginredirecturi=${path}`;
    }
  }
};

// Function to get the current epoch time in milliseconds since the page load
const getEpochTime = (): string => Date.now().toString();

// Function to convert url to a URL object
const convertToUrlObject = (url: RequestInfo): URL => {
  if (typeof url === 'string') {
    return new URL(url, window.location.origin);
  } else if (url instanceof Request) {
    return new URL(url.url, window.location.origin);
  } else {
    throw new Error('Unsupported URL type');
  }
};

const shouldAddEpochTime = (
  customFetchReqOverrideEnabled?: boolean,
  options?: RequestInit
): boolean => {
  if (!customFetchReqOverrideEnabled) return false;
  if (options?.method && options.method.toUpperCase() === 'OPTIONS') return false;
  return true;
};

// Handler to add epoch time to query parameters
const addEpochTimeToUrl = (args: FetchArgs, customFetchReqOverrideEnabled?: boolean) => {
  let [url, options = {} as RequestInit] = args;

  if (!shouldAddEpochTime(customFetchReqOverrideEnabled, options)) {
    return args;
  }

  const epochTime = getEpochTime();
  let urlObj: URL;

  try {
    urlObj = convertToUrlObject(url);
  } catch (error) {
    return [url, options] as FetchArgs;
  }

  urlObj.searchParams.append(EPOCH_TIME_PARAM, epochTime);

  if (url instanceof Request) {
    url = new Request(urlObj.toString(), url);
  } else {
    url = urlObj.toString();
  }

  return [url, options] as FetchArgs;
};

// Create custom fetch function
const createCustomFetch = (customFetchReqOverrideEnabled?: boolean) => {
  const oldFetch = window.fetch;

  const customFetch = (...args: FetchArgs): Promise<Response> =>
    new Promise((resolve, reject) => {
      const modifiedArgs = addEpochTimeToUrl(args, customFetchReqOverrideEnabled);

      oldFetch(...modifiedArgs)
        .then(response => {
          resolve(response);
          handleUnauthorizedError(response); // Handle 401 error
        })
        .catch(error => {
          reject(error);
        });
    });

  // Override the global fetch with the custom fetch
  window.fetch = customFetch as typeof fetch;
};

export default createCustomFetch;
